import React, { useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';

// Simple language switcher: the current channel plus its linked channels.
// The toggle is a black-square header button styled via the shared
// .header__actions-container rules in header.scss (kept in SCSS because it needs
// header-level specificity). The popup is styled with Tailwind (tw- prefixed).
// Data is provided server-side, so there is no API round-trip.
const LanguageSwitch = ({ items, title = 'Language selector' }) => {
  const [open, setOpen] = useState(false);
  const ref = useOnclickOutside(() => setOpen(false));

  if (!items || items.length < 2) {
    return null;
  }

  const current = items.find((item) => item.selected) || items[0];

  return (
    <div className={`lang-switch${open ? ' lang-switch--active' : ''}`} ref={ref}>
      <a
        href="#"
        className="lang-switch__toggle"
        rel="nofollow"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={title}
        title={title}
        onClick={(e) => {
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
      >
        <svg
          className="lang-switch__icon"
          width="22"
          height="21"
          viewBox="0 0 22 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M11.0001 -0.000976562C16.5238 -0.000976562 21.0016 4.47687 21.0016 10.0006C21.0016 15.5243 16.5238 20.0021 11.0001 20.0021C5.47638 20.0021 0.998535 15.5243 0.998535 10.0006C0.998535 4.47687 5.47638 -0.000976562 11.0001 -0.000976562ZM13.939 14.5003H8.06118C8.71322 16.9144 9.84657 18.5021 11.0001 18.5021C12.1536 18.5021 13.287 16.9144 13.939 14.5003ZM6.5084 14.5009L3.78591 14.5007C4.74425 16.0338 6.1777 17.2394 7.88008 17.9114C7.3578 17.0915 6.92681 16.0653 6.60981 14.8959L6.5084 14.5009ZM18.2143 14.5007L15.4918 14.5009C15.168 15.8346 14.7004 17.0004 14.119 17.9114C15.716 17.2814 17.0757 16.1824 18.0291 14.7843L18.2143 14.5007ZM6.09351 7.99992H2.7359L2.73115 8.01713C2.57906 8.65348 2.49854 9.31763 2.49854 10.0006C2.49854 11.0567 2.69112 12.0679 3.0431 13.0009L6.21626 13.0005C6.07396 12.0514 5.99854 11.0432 5.99854 10.0006C5.99854 9.31656 6.031 8.64736 6.09351 7.99992ZM14.397 7.99999H7.60316C7.53514 8.64025 7.49853 9.30997 7.49853 10.0006C7.49853 11.0601 7.58468 12.0704 7.73827 13.0007H14.2619C14.4155 12.0704 14.5016 11.0601 14.5016 10.0006C14.5016 9.30997 14.465 8.64025 14.397 7.99999ZM19.2647 7.99909L15.9067 7.99994C15.9692 8.64736 16.0016 9.31656 16.0016 10.0006C16.0016 11.0432 15.9262 12.0514 15.7839 13.0005L18.9571 13.0009C19.309 12.0679 19.5016 11.0567 19.5016 10.0006C19.5016 9.31117 19.4196 8.64093 19.2647 7.99909ZM7.88114 2.08973L7.85823 2.09844C5.81092 2.91315 4.1549 4.50046 3.25023 6.50033L6.29835 6.50069C6.61171 4.7479 7.15855 3.22198 7.88114 2.08973ZM11.0001 1.49902L10.8844 1.50432C9.61899 1.62008 8.39651 3.62205 7.8288 6.50027H14.1714C13.6052 3.63011 12.388 1.63131 11.1264 1.50534L11.0001 1.49902ZM14.1201 2.08979L14.2269 2.26388C14.8961 3.37635 15.4043 4.83623 15.7018 6.50069L18.7499 6.50033C17.8853 4.58892 16.3343 3.05439 14.4113 2.21106L14.1201 2.08979Z"
            fill="currentColor"
          />
        </svg>
        <span className="lang-switch__label hide">{current.languageName}</span>
      </a>
      {open && (
        <ul className="tw-absolute tw-top-full tw-right-0 tw-z-[100] tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-4 tw-whitespace-nowrap tw-bg-[#333] tw-px-8 tw-pt-4 tw-pb-6 tw-text-white tw-min-w-[200px]">
          {items.map((item, index) => (
            <li className="tw-m-0" key={`lang-${index}`}>
              <a
                href={item.url}
                rel="nofollow"
                className={`tw-relative tw-inline-flex tw-items-center tw-gap-2.5 tw-text-sm tw-no-underline ${
                  item.selected
                    ? "tw-text-ohlins-yellow hover:tw-text-ohlins-yellow after:tw-absolute after:tw-inset-x-0 after:tw-bottom-[-2px] after:tw-h-px after:tw-bg-ohlins-yellow after:tw-content-['']"
                    : 'tw-text-white hover:tw-text-white'
                }`}
              >
                {item.languageImageUrl && (
                  <img
                    className="tw-block tw-h-4 tw-w-[22px] tw-shrink-0 tw-object-contain"
                    src={item.languageImageUrl}
                    alt=""
                  />
                )}
                <span>{item.languageName}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitch;
